import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link2, Copy, ExternalLink, Settings2, CheckCircle2 } from "lucide-react";

/**
 * PublicSignupLinks — surfaces the two permanent, embeddable sign-up
 * URLs (agent + dealer) with copy-to-clipboard shortcuts, plus a
 * super-admin control for configuring which dealer forms ship in the
 * self-signup packet. Rendered at the top of `/admin/agents`.
 */
export default function PublicSignupLinks() {
  const [urls, setUrls] = useState({ agent_apply_url: "", dealer_apply_url: "" });
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: cfg }, { data: settings }, formsRes] = await Promise.all([
        api.get("/public/signup-config"),
        api.get("/admin/public-signup/settings").catch(() => ({ data: { dealer_form_ids: [] } })),
        api.get("/dealer-forms").catch(() => ({ data: { items: [] } })),
      ]);
      // Fall back if window origin differs from the API-reported base
      const origin = window.location.origin;
      setUrls({
        agent_apply_url: cfg?.agent_apply_url || `${origin}/agent/apply`,
        dealer_apply_url: cfg?.dealer_apply_url || `${origin}/dealer/apply`,
      });
      setForms(formsRes?.data?.items || formsRes?.data || []);
      setSelected(settings?.dealer_form_ids || []);
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/admin/public-signup/settings", { dealer_form_ids: selected });
      toast.success("Default packet updated");
      setConfigOpen(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8" data-testid="admin-public-signup-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Link2 size={16} className="text-[#D4AF37]" />
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
            Permanent sign-up links
          </div>
        </div>
        <button
          type="button"
          onClick={() => setConfigOpen((v) => !v)}
          className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6C757D] hover:text-[#0B132B] flex items-center gap-1.5"
          data-testid="admin-public-signup-configure"
        >
          <Settings2 size={12} />
          {configOpen ? "Close" : "Configure dealer packet"}
        </button>
      </div>

      <p className="text-sm text-[#6C757D] leading-relaxed mb-6">
        Embed these URLs on your marketing site or paste them into outreach emails so agents and dealers can sign up on their own. Submissions land in this admin pipeline for review.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LinkRow
          label="Agent producer agreement"
          url={urls.agent_apply_url}
          testid="agent-link"
          onCopy={() => copy(urls.agent_apply_url, "Agent link")}
        />
        <LinkRow
          label="Dealer sign-up"
          url={urls.dealer_apply_url}
          testid="dealer-link"
          onCopy={() => copy(urls.dealer_apply_url, "Dealer link")}
        />
      </div>

      {configOpen && (
        <div className="mt-6 pt-6 border-t border-[#E9ECEF]" data-testid="admin-public-signup-config">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-2">
            Dealer sign-up packet — forms
          </div>
          <p className="text-xs text-[#6C757D] mb-4 leading-relaxed">
            Pick the forms that get attached to every self-signup dealer packet. Applicants are dropped straight into the e-sign flow after submitting.
          </p>
          {forms.length === 0 ? (
            <div className="text-xs text-[#6C757D] italic">No forms in the dealer library yet — upload some in Dealer Forms first.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-auto pr-2">
              {forms.map((f) => {
                const id = f.id || String(f._id || "");
                const isOn = selected.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className={`text-left border rounded-sm p-3 text-sm transition-colors flex items-center gap-2 ${
                      isOn ? "border-[#0B132B] bg-[#0B132B]/5" : "border-[#E9ECEF] hover:border-[#0B132B]/40"
                    }`}
                    data-testid={`admin-public-signup-form-${id}`}
                  >
                    {isOn ? <CheckCircle2 size={16} className="text-[#0B132B]" /> : <span className="w-4 h-4 rounded-full border border-[#E9ECEF]" />}
                    <span className="truncate flex-1 text-[#0B132B]">{f.name || f.filename || "Untitled form"}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setConfigOpen(false)} className="h-9">Cancel</Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-9" data-testid="admin-public-signup-save">
              {saving ? "Saving…" : "Save default packet"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function LinkRow({ label, url, onCopy, testid }) {
  return (
    <div className="border border-[#E9ECEF] rounded-sm p-4" data-testid={`admin-public-signup-${testid}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2">{label}</div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="flex-1 text-xs font-mono px-3 py-2 rounded-sm border border-[#E9ECEF] bg-[#F8F9FA] text-[#0B132B]"
          data-testid={`admin-public-signup-${testid}-input`}
        />
        <button
          type="button"
          onClick={onCopy}
          className="p-2 rounded-sm hover:bg-[#0B132B]/5 text-[#6C757D] hover:text-[#0B132B]"
          title="Copy"
          data-testid={`admin-public-signup-${testid}-copy`}
        >
          <Copy size={14} />
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="p-2 rounded-sm hover:bg-[#0B132B]/5 text-[#6C757D] hover:text-[#0B132B]"
          title="Open"
        >
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}
